import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, Search, Users, Calendar, Clock, Printer, ClipboardList, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RosterLookup = () => {
  const navigate = useNavigate();
  const [bookingCode, setBookingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rosterData, setRosterData] = useState(null);
  const printRef = useRef(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    
    if (!bookingCode.trim()) {
      setError('Please enter a booking code');
      return;
    }

    setLoading(true);
    setError('');
    setRosterData(null);

    try {
      // Add timestamp to prevent caching
      const response = await axios.get(`${API}/roster/${bookingCode.trim()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: {
          _t: Date.now() // Cache buster
        }
      });
      setRosterData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to find roster. Please check your booking code.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Class Roster - ${rosterData?.class_type_name || 'Class'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
              color: #333;
            }
            .header .class-info {
              color: #666;
              font-size: 14px;
            }
            .roster-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .roster-table th,
            .roster-table td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            .roster-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .roster-table tr:nth-child(even) {
              background-color: #fafafa;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #999;
              font-size: 12px;
            }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${rosterData?.class_type_name || 'Class Roster'}</h1>
            <div class="class-info">
              <p>${rosterData?.date || ''} ${rosterData?.time ? `at ${rosterData.time}` : ''}</p>
              <p>Booking Code: ${rosterData?.reference_code?.toUpperCase() || ''}</p>
              <p>Total Students: ${rosterData?.student_count || 0}</p>
            </div>
          </div>
          <table class="roster-table">
            <thead>
              <tr>
                <th style="width: 50px;">#</th>
                <th>Last Name</th>
                <th>First Name</th>
                <th style="width: 150px;">Signature</th>
              </tr>
            </thead>
            <tbody>
              ${rosterData?.roster?.map((student, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${student.last_name}</td>
                  <td>${student.first_name}</td>
                  <td style="text-align: center;">${student.signed_in ? (student.signature ? `<img src="${student.signature}" style="max-height: 30px; max-width: 140px;" alt="Signature" />` : '✓ Signed') : ''}</td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>
          <div class="footer">
            <p>Biggest Little CPR - Class Roster</p>
            <p>Printed on ${new Date().toLocaleDateString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleReset = () => {
    setRosterData(null);
    setBookingCode('');
    setError('');
  };

  return (
    <div className="min-h-screen relative">
      {/* Background Image with Dark Overlay */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10">
      {/* Header */}
      <div className="bg-slate-800/70 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white hover:bg-slate-700"
            data-testid="back-to-booking"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Booking
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 rounded-full mb-4">
            <ClipboardList className="h-8 w-8 text-teal-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Check Class Roster</h1>
          <p className="text-slate-400">Enter your booking code to view the class roster</p>
        </div>

        {!rosterData ? (
          /* Lookup Form */
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Search className="h-5 w-5 text-teal-400" />
                Enter Booking Code
              </CardTitle>
              <CardDescription className="text-slate-400">
                The booking code was provided/created when your class was booked.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLookup} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Enter booking code (e.g., 1234)"
                    value={bookingCode}
                    onChange={(e) => setBookingCode(e.target.value)}
                    className="bg-slate-900 border-teal-600 text-white text-center text-2xl tracking-widest h-14 font-mono"
                    maxLength={10}
                    data-testid="booking-code-input"
                  />
                </div>
                
                {error && (
                  <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !bookingCode.trim()}
                  className="w-full bg-teal-600 hover:bg-teal-500 text-white h-12"
                  data-testid="lookup-roster-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Searching...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      View Roster
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          /* Roster Display */
          <div className="space-y-6" ref={printRef}>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white text-2xl">{rosterData.class_type_name}</CardTitle>
                    <div className="flex flex-wrap gap-4 mt-2 text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {rosterData.date}
                      </span>
                      {rosterData.time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {rosterData.time}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {rosterData.student_count} Student{rosterData.student_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-500">Booking Code</span>
                    <p className="text-lg font-mono text-teal-400 font-bold">{rosterData.reference_code?.toUpperCase()}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Class Roster
                </h3>
                
                {rosterData.roster && rosterData.roster.length > 0 ? (
                  <div className="bg-slate-900/50 rounded-lg border border-slate-700 overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700 bg-slate-800/50">
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider w-12">#</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Last Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">First Name</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-24">Signed In</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {rosterData.roster.map((student, idx) => (
                          <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                            <td className="px-4 py-3 text-slate-500 text-sm">{idx + 1}</td>
                            <td className="px-4 py-3 text-white font-medium">{student.last_name}</td>
                            <td className="px-4 py-3 text-white">{student.first_name}</td>
                            <td className="px-4 py-3 text-center">
                              {student.signed_in ? (
                                <CheckCircle className="h-5 w-5 text-emerald-400 mx-auto" title="Signed in" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-400 mx-auto" title="Not signed in" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    No students registered yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handlePrint}
                className="flex-1 bg-teal-600 hover:bg-teal-500 text-white"
                data-testid="print-roster-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Roster
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="flex-1 border-teal-600 text-white hover:bg-slate-700"
                data-testid="new-lookup-btn"
              >
                <Search className="h-4 w-4 mr-2" />
                Look Up Another Code
              </Button>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 text-center text-sm text-white">
          <p>Can't find your booking code? Contact us at</p>
          <a href="mailto:classes@biggestlittlecpr.com" className="text-teal-400 hover:underline">
            classes@biggestlittlecpr.com
          </a>
          <span className="mx-2">or</span>
          <a href="tel:775-722-8056" className="text-teal-400 hover:underline">
            775-722-8056
          </a>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RosterLookup;
